import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

const SLIDES = Array.from({ length: 33 }, (_, i) => i + 1);

export function TestimonialsCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", dragFree: false, containScroll: "trimSnaps" },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })],
  );

  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    });
  }, [emblaApi, onSelect]);

  const scrollTo = (i: number) => emblaApi?.scrollTo(i);

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div ref={emblaRef} className="overflow-hidden touch-pan-y">
        <div className="flex -ml-3 sm:-ml-4">
          {SLIDES.map((n, idx) => (
            <div
              key={n}
              className="pl-3 sm:pl-4 shrink-0 grow-0 basis-[78%] xs:basis-[60%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
            >
              <div className="relative aspect-[9/16] w-full overflow-hidden rounded-2xl border border-zinc-800/80 shadow-xl bg-neutral-900 p-1.5 md:p-2 flex items-center justify-center">
                <img
                  src={`/testimonials/wa-${n}.webp`}
                  alt={`WhatsApp testimonial ${n} from a student`}
                  loading={idx < 3 ? "eager" : "lazy"}
                  decoding="async"
                  className="h-full w-full object-contain select-none pointer-events-none"
                  draggable={false}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {snaps.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-1.5">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === selected ? "w-5 bg-primary" : "w-1.5 bg-foreground/30 hover:bg-foreground/50",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
